import {
  ArrowRight,
  AtSign,
  Facebook,
  Github,
  Globe2,
  Instagram,
  Link2,
  Linkedin,
  Mail,
  Map,
  MapPin,
  MessageCircle,
  Phone,
  Quote,
  Send,
  Twitter,
  UserRound,
  X,
  Youtube,
  type LucideIcon,
} from 'lucide-react'
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { fullPhone, safeExternalUrl } from '../lib/card'
import type { CardLayout, DigitalCard } from '../types/card'

interface CardCss extends CSSProperties {
  '--card-bg': string
  '--card-text': string
  '--card-accent': string
  '--card-radius': string
  '--card-elevation': string
  '--name-size': string
  '--profession-size': string
  '--intro-size': string
  '--contact-size': string
  '--profile-border-width': string
  '--profile-gradient': string
}

interface SocialItem {
  key: string
  href: string
  icon: LucideIcon
  label: string
}

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'SC'
}

function alpha(hex: string, value: number) {
  const normalized = hex.trim().replace('#', '')
  if (/^[0-9a-f]{3}$/i.test(normalized)) {
    const [r, g, b] = normalized.split('').map((part) => Number.parseInt(`${part}${part}`, 16))
    return `rgba(${r}, ${g}, ${b}, ${value / 255})`
  }
  if (/^[0-9a-f]{6}$/i.test(normalized)) {
    const r = Number.parseInt(normalized.slice(0, 2), 16)
    const g = Number.parseInt(normalized.slice(2, 4), 16)
    const b = Number.parseInt(normalized.slice(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${value / 255})`
  }
  return hex
}

function useGoogleCardFont(fontFamily: string) {
  useEffect(() => {
    if (!/^[a-z0-9 ]+$/i.test(fontFamily)) return
    if (['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Courier New', 'Verdana'].includes(fontFamily)) return
    const id = `sandhi-card-font-${fontFamily.toLowerCase().replace(/\s+/g, '-')}`
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.trim().replace(/\s+/g, '+')}:wght@400;500;600;700&display=swap`
    document.head.appendChild(link)
  }, [fontFamily])
}

export function DigitalCardView({ card, interactive = true }: { card: DigitalCard; interactive?: boolean }) {
  const [profileOpen, setProfileOpen] = useState(false)
  const style = card.card_style ?? {}
  const layout: CardLayout = style.layout || 'standard'
  const accent = style.accentColor || '#356cf8'
  const textColor = style.textColor || '#0b1221'
  const background = style.useGradient
    ? `linear-gradient(to bottom right, ${style.gradientStartColor || style.backgroundColor || '#ffffff'}, ${style.gradientEndColor || accent})`
    : (style.backgroundColor || '#ffffff')
  const fontFamily = style.fontFamily || 'Roboto'
  const imageBorder = style.imageBorderColor || accent
  const profileGradient = `linear-gradient(to bottom right, ${accent}, ${alpha(accent, 150)}, ${alpha(imageBorder, 200)}, ${accent})`
  const css = {
    '--card-bg': background,
    '--card-text': textColor,
    '--card-accent': accent,
    '--card-radius': `${style.borderRadius ?? 12}px`,
    '--card-elevation': `${Math.max(style.elevation ?? 2, 0)}px`,
    '--name-size': `${style.nameSize ?? 28}px`,
    '--profession-size': `${style.professionSize ?? 20}px`,
    '--intro-size': `${style.introductionSize ?? 16}px`,
    '--contact-size': `${style.contactInfoSize ?? 14}px`,
    '--profile-border-width': `${Math.max(style.imageBorderWidth ?? 0, 0) + 3}px`,
    '--profile-gradient': profileGradient,
    background,
    fontFamily: `'${fontFamily}', Roboto, sans-serif`,
  } as CardCss
  const pattern = style.usePatternBackground ? style.patternType || 'dots' : 'none'
  const profileUrl = safeExternalUrl(card.profile_image_url)
  const coverUrl = safeExternalUrl(card.cover_image_url)
  const location = card.full_address || [card.address, card.city, card.state, card.country, card.zip_code].filter(Boolean).join(', ')
  const profession = `${card.profession || ''}${card.company ? ` at ${card.company}` : ''}`.trim()
  const socialItems = getSocialItems(card)

  useGoogleCardFont(fontFamily)

  const contactInfo = (
    <div className="app-card__contact-scroll">
      <div className="app-card__contacts">
        {card.email && <Contact href={`mailto:${card.email}`} icon={<Mail />} text={card.email} interactive={interactive} />}
        {card.phone_number && <Contact href={`tel:${fullPhone(card)}`} icon={<Phone />} text={`${card.country_code || ''} ${card.phone_number}`.trim()} interactive={interactive} />}
        {location && <Contact icon={<MapPin />} text={location} interactive={false} multiline />}
        {card.location_url && <Contact href={safeExternalUrl(card.location_url) || '#'} icon={<Map />} text="View on Map" interactive={interactive} external />}
      </div>
      <SocialIcons items={socialItems} interactive={interactive} />
    </div>
  )
  const name = <h1 className="app-card__name">{card.name}</h1>
  const job = <p className="app-card__profession">{profession}</p>
  const introduction = <IntroductionPanel text={card.introduction} />

  return (
    <>
      <article
        className={`digital-card app-card--${layout} pattern--${pattern} ${coverUrl ? 'app-card--has-cover' : 'app-card--no-cover'} ${style.useShadowText ? 'app-card--shadow-text' : ''}`}
        style={css}
      >
        {layout === 'standard' && (
          <>
            <CardCover coverUrl={coverUrl} />
            <ProfileImage card={card} profileUrl={profileUrl} layout={layout} interactive={interactive} onOpen={() => setProfileOpen(true)} />
            <div className="app-card__content app-card__content--standard">
              <div className="app-card__profile-space app-card__profile-space--standard" />
              {name}
              {job}
              <Divider />
              {contactInfo}
              <Divider />
              {introduction}
            </div>
          </>
        )}

        {layout === 'modern' && (
          <>
            <CardCover coverUrl={coverUrl} />
            <ProfileImage card={card} profileUrl={profileUrl} layout={layout} interactive={interactive} onOpen={() => setProfileOpen(true)} />
            <div className="app-card__modern-header">
              <div className="app-card__profile-space app-card__profile-space--modern" />
              {name}
              {job}
            </div>
            <div className="app-card__content app-card__content--modern">
              {contactInfo}
              {introduction}
            </div>
          </>
        )}

        {layout === 'minimal' && (
          <div className="app-card__content app-card__content--minimal">
            <div className="app-card__minimal-header">
              <MinimalProfileImage card={card} profileUrl={profileUrl} interactive={interactive} onOpen={() => setProfileOpen(true)} />
              <div>
                <h1 className="app-card__minimal-name">{card.name}</h1>
                <p className="app-card__minimal-profession">{card.profession}</p>
              </div>
            </div>
            <MinimalContacts card={card} location={location} items={socialItems} interactive={interactive} />
            <MinimalIntroduction text={card.introduction} />
          </div>
        )}

        {layout === 'creative' && (
          <>
            {coverUrl && <div className="app-card__cover" style={{ backgroundImage: `url(${coverUrl})` }} />}
            <ProfileImage card={card} profileUrl={profileUrl} layout={layout} interactive={interactive} onOpen={() => setProfileOpen(true)} />
            <div className="app-card__content app-card__content--creative">
              {name}
              {job}
              {contactInfo}
              {introduction}
            </div>
          </>
        )}
      </article>

      {profileOpen && profileUrl && (
        <div className="profile-viewer" role="dialog" aria-modal="true" aria-label={`${card.name} profile photo`} onClick={() => setProfileOpen(false)}>
          <button type="button" aria-label="Close profile photo" onClick={() => setProfileOpen(false)}><X /></button>
          <img src={profileUrl} alt={card.name} onClick={(event) => event.stopPropagation()} />
          <div><strong>{card.name}</strong><span>{card.profession}</span></div>
        </div>
      )}
    </>
  )
}

function CardCover({ coverUrl }: { coverUrl: string | null }) {
  return coverUrl
    ? <div className="app-card__cover" style={{ backgroundImage: `url(${coverUrl})` }} />
    : <div className="app-card__cover-spacer" />
}

function ProfileImage({ card, profileUrl, layout, interactive, onOpen }: { card: DigitalCard; profileUrl: string | null; layout: CardLayout; interactive: boolean; onOpen: () => void }) {
  const content = profileUrl ? <img src={profileUrl} alt={card.name} /> : <><UserRound /><span>{initials(card.name)}</span></>
  if (interactive && profileUrl) {
    return <button type="button" className={`app-card__avatar app-card__avatar--${layout}`} onClick={onOpen} aria-label={`Open ${card.name}'s profile photo`}>{content}</button>
  }
  return <div className={`app-card__avatar app-card__avatar--${layout}`}>{content}</div>
}

function MinimalProfileImage({ card, profileUrl, interactive, onOpen }: { card: DigitalCard; profileUrl: string | null; interactive: boolean; onOpen: () => void }) {
  const content = profileUrl ? <img src={profileUrl} alt={card.name} /> : <UserRound />
  if (interactive && profileUrl) return <button type="button" className="app-card__minimal-avatar" onClick={onOpen} aria-label={`Open ${card.name}'s profile photo`}>{content}</button>
  return <div className="app-card__minimal-avatar">{content}</div>
}

function Divider() {
  return <div className="app-card__divider" aria-hidden="true" />
}

function IntroductionPanel({ text }: { text: string }) {
  if (!text) return null
  return (
    <div className="app-card__introduction-panel">
      <div className="app-card__about-label"><Quote /><span>ABOUT</span></div>
      <p className="app-card__introduction">{text}</p>
    </div>
  )
}

function Contact({ href, icon, text, interactive, external = false, multiline = false }: { href?: string; icon: ReactNode; text: string; interactive: boolean; external?: boolean; multiline?: boolean }) {
  const className = `app-card__contact ${multiline ? 'app-card__contact--multiline' : ''}`
  const content = <><span className="app-card__contact-icon">{icon}</span><span className="app-card__contact-text">{text}</span>{interactive && href && <ArrowRight className="app-card__contact-arrow" />}</>
  if (!interactive || !href) return <div className={className}>{content}</div>
  return <a className={className} href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>{content}</a>
}

function MinimalContacts({ card, location, items, interactive }: { card: DigitalCard; location: string; items: SocialItem[]; interactive: boolean }) {
  return (
    <div className="app-card__minimal-contacts">
      {card.email && <MinimalContact href={`mailto:${card.email}`} icon={<Mail />} text={card.email} interactive={interactive} />}
      {card.phone_number && <MinimalContact href={`tel:${fullPhone(card)}`} icon={<Phone />} text={`${card.country_code || ''} ${card.phone_number}`.trim()} interactive={interactive} />}
      {location && <MinimalContact icon={<MapPin />} text={location} interactive={false} />}
      {card.location_url && <MinimalContact href={safeExternalUrl(card.location_url) || '#'} icon={<Map />} text="View on Map" interactive={interactive} external />}
      <SocialIcons items={items} interactive={interactive} />
    </div>
  )
}

function MinimalContact({ href, icon, text, interactive, external = false }: { href?: string; icon: ReactNode; text: string; interactive: boolean; external?: boolean }) {
  const content = <><span>{icon}</span><b>{text}</b></>
  if (!interactive || !href) return <div className="app-card__minimal-contact">{content}</div>
  return <a className="app-card__minimal-contact" href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>{content}</a>
}

function MinimalIntroduction({ text }: { text: string }) {
  if (!text) return null
  return <div className="app-card__minimal-introduction"><span>About</span><p>{text}</p></div>
}

function SocialIcons({ items, interactive }: { items: SocialItem[]; interactive: boolean }) {
  if (!items.length) return null
  return (
    <div className="app-card__socials">
      {items.map(({ key, href, icon: Icon, label }) => interactive
        ? <a key={key} href={href} target="_blank" rel="noreferrer" aria-label={label} title={label}><Icon /></a>
        : <span key={key} aria-label={label}><Icon /></span>)}
    </div>
  )
}

function getSocialItems(card: DigitalCard): SocialItem[] {
  const definitions: Array<[keyof DigitalCard, string, LucideIcon, string]> = [
    ['website_url', 'website', Globe2, 'Website'],
    ['linkedin_url', 'linkedin', Linkedin, 'LinkedIn'],
    ['github_url', 'github', Github, 'GitHub'],
    ['twitter_url', 'twitter', Twitter, 'Twitter'],
    ['instagram_url', 'instagram', Instagram, 'Instagram'],
    ['facebook_url', 'facebook', Facebook, 'Facebook'],
    ['youtube_url', 'youtube', Youtube, 'YouTube'],
    ['tiktok_url', 'tiktok', AtSign, 'TikTok'],
    ['telegram_url', 'telegram', Send, 'Telegram'],
    ['snapchat_url', 'snapchat', MessageCircle, 'Snapchat'],
    ['pinterest_url', 'pinterest', Link2, 'Pinterest'],
    ['behance_url', 'behance', Link2, 'Behance'],
    ['dribbble_url', 'dribbble', Link2, 'Dribbble'],
    ['medium_url', 'medium', Link2, 'Medium'],
    ['discord_url', 'discord', MessageCircle, 'Discord'],
    ['reddit_url', 'reddit', MessageCircle, 'Reddit'],
    ['clubhouse_url', 'clubhouse', MessageCircle, 'Clubhouse'],
    ['spotify_url', 'spotify', Link2, 'Spotify'],
    ['soundcloud_url', 'soundcloud', Link2, 'SoundCloud'],
    ['twitch_url', 'twitch', Link2, 'Twitch'],
    ['vimeo_url', 'vimeo', Link2, 'Vimeo'],
  ]
  const items = definitions.flatMap(([field, key, icon, label]) => {
    const value = card[field]
    if (typeof value !== 'string' || !value.trim()) return []
    const href = safeExternalUrl(value)
    return href ? [{ key, href, icon, label }] : []
  })
  if (card.whatsapp_number?.trim()) {
    const number = card.whatsapp_number.replace(/[^\d]/g, '')
    if (number) items.push({ key: 'whatsapp', href: `https://wa.me/${number}`, icon: MessageCircle, label: 'WhatsApp' })
  }
  return items
}
